<script setup lang="ts">
import type { BrokerPerformanceProps } from '.';

import { cn } from '@/utils';

import { BrokerHighlights } from '~/components/BrokerHighlights';
import { Skeleton } from '~/components/ui/skeleton';
import Tag from '~/components/ui/tag/Tag.vue';

import IconPilarCrown from '~icons/pilar/crown';

const SKELETON_CHIP_GROUPS = [
	['w-[98px]', 'w-[116px]', 'w-[96px]', 'w-[116px]', 'w-[149px]'],
	['w-[87px]', 'w-[111px]', 'w-[161px]']
];

const props = withDefaults(defineProps<BrokerPerformanceProps>(), {
	isLoading: false,
	regions: () => [],
	propertyTypes: () => [],
	numbers: () => [],
	title: 'Atuação e resultados',
	regionsTitle: 'Áreas de atuação',
	propertyTypesTitle: 'Especialidades',
	numbersTitle: 'Números'
});

const hasContent = computed<boolean>(
	() =>
		props.regions.length > 0 ||
		props.propertyTypes.length > 0 ||
		props.numbers.length > 0
);
</script>

<template>
	<section
		v-if="isLoading || hasContent"
		:class="
			cn(
				'flex w-full flex-col gap-6 border-b border-white-dark-3 pb-8 lg:pb-12',
				props.class
			)
		"
		:aria-busy="isLoading"
	>
		<div
			v-if="isLoading"
			class="flex h-9 items-center lg:h-12"
			aria-hidden="true"
		>
			<Skeleton
				is-active
				variant="white"
				class="h-[29px] w-[296px] max-w-full rounded-none motion-reduce:animate-none lg:translate-y-0.5"
			/>
		</div>
		<h2
			v-else
			class="font-matter-sq text-2xl font-normal leading-[1.5] tracking-[-0.02em] text-primary lg:text-[32px]"
		>
			{{ title }}
		</h2>

		<div v-if="isLoading" class="flex w-full flex-col gap-6" aria-hidden="true">
			<div
				v-for="(chipWidths, group) in SKELETON_CHIP_GROUPS"
				:key="`broker-performance-skeleton-group-${group}`"
				class="flex flex-col gap-4"
			>
				<div class="flex h-6 items-center">
					<Skeleton
						is-active
						variant="white"
						class="h-[18px] w-[133px] rounded-none motion-reduce:animate-none"
					/>
				</div>
				<div class="flex flex-wrap gap-2">
					<Skeleton
						v-for="(width, chip) in chipWidths"
						:key="`broker-performance-skeleton-chip-${group}-${chip}`"
						is-active
						variant="white"
						:class="['h-8 rounded-none motion-reduce:animate-none', width]"
					/>
				</div>
			</div>

			<div class="flex flex-col gap-4 pt-4">
				<div class="flex h-[30px] items-center">
					<Skeleton
						is-active
						variant="white"
						class="h-[18px] w-[133px] rounded-none motion-reduce:animate-none"
					/>
				</div>
				<div class="grid grid-cols-2 gap-3 2xl:grid-cols-4">
					<Skeleton
						v-for="card in 4"
						:key="`broker-performance-skeleton-card-${card}`"
						is-active
						variant="white"
						class="h-[118px] w-full rounded-none motion-reduce:animate-none"
					/>
				</div>
			</div>
		</div>

		<div v-else class="flex w-full flex-col gap-6">
			<div v-if="regions.length" class="flex flex-col gap-4">
				<h3
					class="font-sans text-base font-normal leading-[150%] tracking-normal text-primary"
				>
					{{ regionsTitle }}
				</h3>

				<ul class="flex flex-wrap gap-2">
					<li v-for="region in regions" :key="region.name">
						<!-- O `Tag` não é auto-import: o `index.ts` da pasta
						     exporta só o `tagVariants`. Sem o import no script ele
						     não resolve, vira elemento desconhecido e o conteúdo
						     do slot é descartado. -->
						<Tag
							:class="[
								'h-8 px-2',
								region.highlighted ? 'bg-secondary' : 'bg-white-dark-1'
							]"
							text-class="text-sm font-normal leading-[150%] tracking-normal text-primary"
						>
							<template v-if="region.highlighted" #icon>
								<IconPilarCrown class="h-[15px] w-[17px] shrink-0" />
							</template>
							{{ region.name }}
						</Tag>
					</li>
				</ul>
			</div>

			<div v-if="propertyTypes.length" class="flex flex-col gap-4">
				<h3
					class="font-sans text-base font-normal leading-[150%] tracking-normal text-primary"
				>
					{{ propertyTypesTitle }}
				</h3>

				<ul class="flex flex-wrap gap-2">
					<li v-for="propertyType in propertyTypes" :key="propertyType.type">
						<Tag
							class="h-8 bg-white-dark-1 px-2"
							text-class="text-sm font-normal leading-[150%] tracking-normal text-primary"
						>
							{{ propertyType.label }}
						</Tag>
					</li>
				</ul>
			</div>

			<div v-if="numbers.length" class="flex flex-col gap-4 pt-4">
				<h3
					class="font-matter-sq text-xl font-normal leading-[150%] tracking-[-0.02em] text-primary"
				>
					{{ numbersTitle }}
				</h3>

				<BrokerHighlights :items="numbers" variant="light" layout="stretch" />
			</div>
		</div>
	</section>
</template>
